import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { getDateFnsLocale } from '@/utils/dateFnsLocale';
import { CalendarDaysIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { CalendarRsvpChoice, normalizeCalendarRsvpValue } from '@/components/calendar/CalendarRsvpChoice';
import { calendarApi, formatCalendarRsvpLabel, type CalendarEventDto } from '@/services/calendarApi';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { cn } from '@/utils/cn';

function apiErr(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { error?: { message?: string }; message?: string } } };
  const m = err?.response?.data?.error?.message ?? err?.response?.data?.message;
  return typeof m === 'string' && m.length > 0 ? m : fallback;
}

type Props = {
  /** Max events to list (default 3 in compact mode, 10 otherwise). */
  maxItems?: number;
  /**
   * Match Key Risks / Issues / Actions: one column width, short list, footer CTA — same as other dashboard modules.
   */
  compact?: boolean;
};

/**
 * Month-at-a-glance agenda for the dashboard (same data as Calendar → Agenda).
 */
export const DashboardAgendaWidget: React.FC<Props> = ({ maxItems: maxItemsProp, compact = false }) => {
  const { user } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const dfLocale = useMemo(() => getDateFnsLocale(currentLanguage.id), [currentLanguage.id]);
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Recurring instance: choose decline this occurrence vs whole series. */
  const [declineTarget, setDeclineTarget] = useState<CalendarEventDto | null>(null);

  const maxItems = maxItemsProp ?? (compact ? 3 : 10);

  const range = useMemo(() => {
    const now = new Date();
    return { from: startOfMonth(now), to: endOfMonth(now) };
  }, []);

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: body } = await calendarApi.list(range.from.toISOString(), range.to.toISOString(), false);
      const list = body.data ?? [];
      const sorted = [...list].sort(
        (a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime()
      );
      setEvents(sorted);
    } catch (e) {
      setEvents([]);
      setError(apiErr(e, t('calendar.error.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [user?.id, range.from, range.to, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = events.slice(0, maxItems);
  const more = Math.max(0, events.length - visible.length);
  const monthLabel = format(range.from, 'MMMM yyyy', { locale: dfLocale });

  const openInCalendar = useCallback(
    (ev: CalendarEventDto) => {
      navigate(`/calendar?openEvent=${encodeURIComponent(ev.id)}`);
    },
    [navigate]
  );

  const isRecurringInstance = useCallback((ev: CalendarEventDto) => {
    return ev.id.startsWith('occ:') && Boolean(ev.seriesEventId);
  }, []);

  const applyRsvp = useCallback(
    async (ev: CalendarEventDto, v: 'none' | 'accepted' | 'declined' | 'tentative', scope: 'series' | 'this_occurrence') => {
      try {
        await calendarApi.setMyRsvp(ev.id, v, { rsvpScope: scope });
        toast.success(t('calendar.toast.rsvpUpdated'));
        void load();
      } catch (err) {
        toast.error(apiErr(err, t('calendar.error.updateRsvpFailed')));
      }
    },
    [load, t]
  );

  const onInviteeRsvpSelect = useCallback(
    (ev: CalendarEventDto, v: 'none' | 'accepted' | 'declined' | 'tentative') => {
      if (v === 'declined' && isRecurringInstance(ev)) {
        setDeclineTarget(ev);
        return;
      }
      void applyRsvp(ev, v, 'series');
    },
    [applyRsvp, isRecurringInstance]
  );

  return (
    <>
    <Card
      className={cn(
        'w-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] shadow-none',
        compact && 'flex h-full flex-col'
      )}
    >
      <CardHeader className={cn(compact && 'space-y-1 pb-3')}>
        <CardTitle className="flex items-center text-[var(--color-surface-text)]">
          <CalendarDaysIcon className="mr-2 h-5 w-5 shrink-0" aria-hidden />
          {t('calendar.dashboard.title')}
        </CardTitle>
        <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
          {compact ? t('calendar.dashboard.subtitleCompact', { month: monthLabel }) : t('calendar.dashboard.subtitleFull', { month: monthLabel })}
        </CardDescription>
      </CardHeader>
      <CardContent className={cn(compact && 'flex flex-1 flex-col')}>
        {loading && (
          <div className="space-y-2 py-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-[var(--color-surface-elevated)]" />
            ))}
          </div>
        )}
        {!loading && error && <p className="text-sm text-[var(--color-error)]">{error}</p>}
        {!loading && !error && events.length === 0 && (
          <div className="py-4 text-center">
            <CalendarDaysIcon className="mx-auto mb-2 h-10 w-10 opacity-40 text-[var(--color-secondary-text)]" />
            <p className="text-sm opacity-75 text-[var(--color-secondary-text)]">{t('calendar.dashboard.empty')}</p>
          </div>
        )}
        {!loading && !error && visible.length > 0 && (
          <div className={cn('space-y-2', compact && 'max-h-[min(280px,42vh)] overflow-y-auto pr-0.5')}>
            {visible.map((ev) => (
              <div
                key={ev.id}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-2 transition-colors hover:bg-[var(--color-accent-bg)]/10"
              >
                <button
                  type="button"
                  className="w-full text-left outline-none ring-[var(--color-info)] focus-visible:ring-2"
                  onClick={() => openInCalendar(ev)}
                >
                  <h4 className="line-clamp-2 text-sm font-medium text-[var(--color-primary)]">{ev.title}</h4>
                  <p className="mt-0.5 text-[10px] text-[var(--color-secondary)] sm:text-xs">
                    {format(parseISO(ev.startAt), 'MMM d', { locale: dfLocale })} ·{' '}
                    {format(parseISO(ev.startAt), 'p', { locale: dfLocale })}
                  </p>
                </button>
                {ev.isInvited && (
                  <div className="mt-1.5 space-y-1" onClick={(e) => e.stopPropagation()}>
                    {ev.mySeriesRsvp != null &&
                      ev.myRsvp != null &&
                      String(ev.mySeriesRsvp) !== String(ev.myRsvp) && (
                        <p className="text-[9px] leading-tight text-[var(--color-secondary)]">
                          {t('calendar.dashboard.seriesLine', {
                            series: formatCalendarRsvpLabel(ev.mySeriesRsvp, t),
                            occurrence: formatCalendarRsvpLabel(ev.myRsvp, t)
                          })}
                        </p>
                      )}
                    <CalendarRsvpChoice
                      compact
                      value={normalizeCalendarRsvpValue(ev.myRsvp)}
                      onSelect={(v) => onInviteeRsvpSelect(ev, v)}
                      aria-label={t('calendar.dashboard.aria.rsvp')}
                      suffix={
                        <span
                          className="ml-0.5 inline-flex shrink-0 items-center rounded border border-sky-500/40 bg-sky-100/70 px-1.5 py-0.5 text-[9px] font-medium uppercase leading-none text-sky-950 dark:bg-sky-900/35 dark:text-sky-100"
                          aria-label={t('calendar.dashboard.aria.invited')}
                        >
                          {t('calendar.dashboard.badge.invited')}
                        </span>
                      }
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!loading && !error && more > 0 && (
          <p className="mt-2 text-[10px] text-[var(--color-secondary)] sm:text-xs">
            {t('calendar.dashboard.moreLine', { count: more, month: monthLabel })}
          </p>
        )}

        <div className="mt-4 border-t border-[var(--color-border)] pt-4">
          <Link
            to="/calendar"
            className="inline-flex items-center justify-center rounded-md bg-[var(--color-accent-bg)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-text)] transition-colors hover:bg-opacity-90"
          >
            <ArrowTopRightOnSquareIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            {t('calendar.dashboard.openCalendar')}
          </Link>
        </div>
      </CardContent>
    </Card>

    {declineTarget && (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agenda-decline-recurring-title"
      >
        <div className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4 shadow-xl">
          <h3 id="agenda-decline-recurring-title" className="text-sm font-semibold text-[var(--color-primary)]">
            {t('calendar.dashboard.decline.title')}
          </h3>
          <p className="mt-2 text-xs text-[var(--color-secondary)]">{t('calendar.dashboard.decline.body')}</p>
          <p className="mt-1 text-[10px] text-[var(--color-secondary)]">
            {declineTarget.title} ·{' '}
            {format(parseISO(declineTarget.startAt), 'MMM d, p', { locale: dfLocale })}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]"
              onClick={() => setDeclineTarget(null)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-3 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]"
              onClick={() => {
                const ev = declineTarget;
                setDeclineTarget(null);
                void applyRsvp(ev!, 'declined', 'this_occurrence');
              }}
            >
              {t('calendar.decline.onlyOccurrence')}
            </button>
            <button
              type="button"
              className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
              onClick={() => {
                const ev = declineTarget;
                setDeclineTarget(null);
                void applyRsvp(ev!, 'declined', 'series');
              }}
            >
              {t('calendar.decline.entireSeries')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
